/**
 * Date utility functions
 */

/**
 * Format date to short date string (MM/DD/YYYY)
 */
export function formatShortDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format date to long date string (Month DD, YYYY)
 */
export function formatLongDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format date to ISO string (YYYY-MM-DD) in UTC (avoid for API date-range filters).
 */
export function formatIsoDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  return d.toISOString().split('T')[0];
}

/**
 * Calendar date as YYYY-MM-DD in the user's local timezone (for inclusive API date filters).
 */
export function formatLocalDateOnly(date: Date | string | null | undefined): string {
  const parts = getLocalDateParts(date);
  if (!parts) return '';
  const mo = String(parts.m + 1).padStart(2, '0');
  const d = String(parts.d).padStart(2, '0');
  return `${parts.y}-${mo}-${d}`;
}

/**
 * Format date with time (MM/DD/YYYY HH:MM AM/PM)
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  return d.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get relative time string (e.g., "2 days ago", "in 3 months")
 */
export function getRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  const isPast = diffMs < 0;
  const absDiff = Math.abs(diffMs);

  if (absDiff < 60000) return 'just now';
  if (absDiff < 3600000) {
    const mins = Math.abs(diffMinutes);
    return isPast ? `${mins} minute${mins !== 1 ? 's' : ''} ago` : `in ${mins} minute${mins !== 1 ? 's' : ''}`;
  }
  if (absDiff < 86400000) {
    const hours = Math.abs(diffHours);
    return isPast ? `${hours} hour${hours !== 1 ? 's' : ''} ago` : `in ${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  if (absDiff < 2592000000) {
    const days = Math.abs(diffDays);
    return isPast ? `${days} day${days !== 1 ? 's' : ''} ago` : `in ${days} day${days !== 1 ? 's' : ''}`;
  }
  if (absDiff < 31536000000) {
    const months = Math.abs(diffMonths);
    return isPast ? `${months} month${months !== 1 ? 's' : ''} ago` : `in ${months} month${months !== 1 ? 's' : ''}`;
  }
  const years = Math.abs(diffYears);
  return isPast ? `${years} year${years !== 1 ? 's' : ''} ago` : `in ${years} year${years !== 1 ? 's' : ''}`;
}

/**
 * Check if date is today
 */
export function isToday(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  
  const today = new Date();
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  );
}

/**
 * Check if date is in the past
 */
export function isPast(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  
  return d.getTime() < Date.now();
}

/**
 * Check if date is in the future
 */
export function isFuture(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return false;
  
  return d.getTime() > Date.now();
}

/**
 * Get days between two dates
 */
export function getDaysBetween(startDate: Date | string, endDate: Date | string): number {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Add days to a date
 */
export function addDays(date: Date | string, days: number): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/**
 * Get start of day
 */
export function getStartOfDay(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Get end of day
 */
export function getEndOfDay(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(date) : new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

/**
 * Local calendar year/month/day for a datepicker value or API instant.
 *
 * - `Date` from Angular Material is usually local midnight for the picked day — use its local Y/M/D.
 * - A **date-only** string `YYYY-MM-DD` is interpreted as that calendar day in the **local** timezone
 *   (not UTC midnight, which shifts to the previous day in negative-offset zones).
 * - Other strings fall through to `Date.parse` then local Y/M/D.
 */
export function getLocalDateParts(
  dateInput: Date | string | null | undefined
): { y: number; m: number; d: number } | null {
  if (dateInput == null) return null;
  if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) return null;
    return {
      y: dateInput.getFullYear(),
      m: dateInput.getMonth(),
      d: dateInput.getDate(),
    };
  }
  const s = String(dateInput).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return { y, m: mo - 1, d };
  }
  const parsed = new Date(s);
  if (isNaN(parsed.getTime())) return null;
  return {
    y: parsed.getFullYear(),
    m: parsed.getMonth(),
    d: parsed.getDate(),
  };
}

/**
 * Parse a launch schedule timestamp from the API.
 * - Preferred: timezone-naive wall-clock (`YYYY-MM-DDTHH:mm:ss`) — the clock time the user selected.
 * - Legacy values with `Z` or a numeric offset are parsed as UTC instants (from older `toISOString()` clients).
 */
export function parseLaunchScheduleDateTime(value: string | null | undefined): Date {
  if (!value) return new Date(NaN);
  const s = String(value).trim();
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    return new Date(s);
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (m) {
    return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0, 0);
  }
  return new Date(s);
}

/** Format launch schedule date/time for staff/customer cards (handles API instant + legacy wall-clock). */
export function formatLaunchScheduleDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? parseLaunchScheduleDateTime(value) : value;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format launch schedule time only. */
export function formatLaunchScheduleTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? parseLaunchScheduleDateTime(value) : value;
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Minute bucket for comparing whether a schedule changed (local wall-clock). */
export function launchScheduleMinuteKey(value: string | Date | null | undefined): number | null {
  const d = typeof value === 'string' ? parseLaunchScheduleDateTime(value) : value;
  if (!d || isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / 60000);
}

/**
 * Combine a calendar date (Material datepicker / `YYYY-MM-DD`) with `HH:mm` (24h) in the user's
 * local timezone. Use {@link formatLaunchScheduleForApi} on the result when sending to the API.
 */
export function combineLocalDateAndTime(
  dateInput: Date | string | null | undefined,
  timeHHmm: string | null | undefined
): Date | null {
  const parts = getLocalDateParts(dateInput);
  if (!parts || !timeHHmm || typeof timeHHmm !== 'string' || !timeHHmm.includes(':')) {
    return null;
  }
  const [hStr, mStr] = timeHHmm.split(':');
  const h = parseInt(hStr, 10);
  const min = parseInt(mStr, 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return new Date(parts.y, parts.m, parts.d, h, min, 0, 0);
}

/**
 * Format a launch schedule Date for the API as a timezone-naive wall-clock string
 * (`YYYY-MM-DDTHH:mm:ss`, no `Z`). Launch schedules are stored/displayed as the clock time the
 * user selected — do not use `toISOString()` (that shifts the saved time by the UTC offset).
 */
export function formatLaunchScheduleForApi(date: Date): string {
  if (!date || isNaN(date.getTime())) {
    throw new Error('Invalid launch schedule date');
  }
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const mi = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${d}T${h}:${mi}:${s}`;
}

/** Default occupancy when a launch is not overnight / has no projected return. */
export const DEFAULT_LAUNCH_OCCUPANCY_HOURS = 2;

/**
 * Minimum lead time before a requested launch slot (marina wall-clock).
 * Matches API enforcement: if it is 15:00, the earliest allowed request is 16:00.
 */
export const LAUNCH_MIN_LEAD_TIME_MINUTES = 60;

/** Statuses that no longer reserve the vessel schedule (aligned with API). */
export function launchReservesScheduleSlot(status: string | null | undefined): boolean {
  const s = (status || '').trim().toUpperCase();
  return s !== 'CANCELLED' && s !== 'RETURNED' && s !== 'COMPLETED';
}

/** Minimal launch row shape for schedule conflict helpers. */
export type LaunchScheduleRow = {
  id?: string;
  status?: string | null;
  requestType?: string | null;
  requestedDateTime?: Date | string | null;
};

/** Compare calendar day using wall-clock date parts (matches API SameWallClockCalendarDay). */
export function sameWallClockCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * Returns an in-flight wash rack request for the vessel on the target date, if any.
 * Business rule: one active wash rack per vessel per calendar day;
 * completed/returned/cancelled do not block; different dates do not block.
 */
export function findActiveWashRackLaunch<T extends LaunchScheduleRow>(
  launches: readonly T[],
  options?: { excludeLaunchId?: string; targetDate?: Date | string | null }
): T | null {
  const exclude = options?.excludeLaunchId?.trim().toLowerCase();
  const targetRaw = options?.targetDate;
  const targetDate =
    targetRaw == null
      ? null
      : typeof targetRaw === 'string'
        ? parseLaunchScheduleDateTime(targetRaw)
        : targetRaw;

  for (const launch of launches) {
    if (exclude && launch.id?.trim().toLowerCase() === exclude) {
      continue;
    }
    if (!launchReservesScheduleSlot(launch.status)) {
      continue;
    }
    if ((launch.requestType || '').trim().toLowerCase() !== 'wash_rack') {
      continue;
    }
    if (targetDate && !Number.isNaN(targetDate.getTime())) {
      if (!launch.requestedDateTime) {
        continue;
      }
      const rawRequested = launch.requestedDateTime;
      const existing =
        typeof rawRequested === 'string'
          ? parseLaunchScheduleDateTime(rawRequested)
          : rawRequested;
      if (Number.isNaN(existing.getTime()) || !sameWallClockCalendarDay(existing, targetDate)) {
        continue;
      }
    }
    return launch;
  }
  return null;
}

/**
 * Occupancy end for conflict checks: projected return when overnight, otherwise start + 2h.
 */
export function getLaunchOccupancyEnd(
  start: Date,
  options?: {
    isStayingOvernight?: boolean | null;
    projectedReturnDateTime?: Date | string | null;
  }
): Date {
  const overnight = !!options?.isStayingOvernight;
  const projectedRaw = options?.projectedReturnDateTime;
  const projected =
    typeof projectedRaw === 'string'
      ? parseLaunchScheduleDateTime(projectedRaw)
      : projectedRaw ?? null;
  if (overnight && projected && !Number.isNaN(projected.getTime()) && projected.getTime() > start.getTime()) {
    return projected;
  }
  return new Date(start.getTime() + DEFAULT_LAUNCH_OCCUPANCY_HOURS * 60 * 60 * 1000);
}

/** Half-open style overlap: [aStart, aEnd) overlaps [bStart, bEnd). */
export function wallClockIntervalsOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  return aStart.getTime() < bEnd.getTime() && bStart.getTime() < aEnd.getTime();
}

/**
 * True when requested wall-clock datetime is sooner than the minimum lead time
 * (includes past times). Example: at 15:00 with a 60-minute lead, 15:45 is too soon; 16:00 is ok.
 */
export function isLaunchRequestedDateTimeInPast(
  requested: Date,
  now: Date = new Date(),
  minLeadMinutes: number = LAUNCH_MIN_LEAD_TIME_MINUTES
): boolean {
  if (Number.isNaN(requested.getTime())) return true;
  return requested.getTime() < now.getTime() + minLeadMinutes * 60 * 1000;
}

/**
 * Build a Date whose local Y/M/D/H/M/S equal the wall-clock time in `timeZone` at `now`.
 * Aligns launch schedule UI with marina-local "now" the same way the API enforces lead time.
 */
export function getWallClockNowInTimeZone(
  timeZone: string | null | undefined,
  now: Date = new Date()
): Date {
  const tz = (timeZone || '').trim() || undefined;
  if (!tz) return now;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).formatToParts(now);

    const map: Record<string, string> = {};
    for (const p of parts) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }

    const y = Number(map['year']);
    const m = Number(map['month']);
    const d = Number(map['day']);
    const h = Number(map['hour']);
    const mi = Number(map['minute']);
    const s = Number(map['second']);
    if (![y, m, d, h, mi, s].every((n) => Number.isFinite(n))) return now;

    return new Date(y, m - 1, d, h, mi, s, 0);
  } catch {
    return now;
  }
}

/**
 * Filter HH:mm options so slots sooner than the minimum lead time are excluded.
 * Uses combineLocalDateAndTime per slot (does not rely on a separate "is today?" compare,
 * which can fail when datepicker values are timezone-shifted).
 */
export function filterPastLaunchTimeOptions(
  times: string[],
  dateVal: Date | string | null | undefined,
  now: Date = new Date(),
  minLeadMinutes: number = LAUNCH_MIN_LEAD_TIME_MINUTES
): string[] {
  if (!dateVal || !times?.length) return times || [];

  return times.filter((t) => {
    const slot = combineLocalDateAndTime(dateVal, t);
    if (!slot || Number.isNaN(slot.getTime())) return false;
    return !isLaunchRequestedDateTimeInPast(slot, now, minLeadMinutes);
  });
}

